/**
 * Global HTTP concurrency limiter.
 *
 * Browsers allow ~6 connections per origin (HTTP/1.1). During sync the app
 * fires 20-30+ concurrent requests across DynamicsWebApi (XHR) and Angular
 * HttpClient, causing most requests to queue in the browser's network stack.
 *
 * This semaphore sits above both HTTP paths so at most `maxConcurrent`
 * requests are actually in-flight at any time, leaving headroom for
 * user-initiated requests.
 */

const DEFAULT_MAX_CONCURRENT = 8;

let running = 0;
let maxConcurrent = DEFAULT_MAX_CONCURRENT;
const queue: (() => void)[] = [];

/** Update the concurrency limit at runtime (e.g. based on network quality). */
export function setHttpConcurrency(limit: number): void {
  maxConcurrent = Math.max(1, limit);
}

/** Current snapshot for diagnostics / profiling. */
export function getHttpSemaphoreStats(): { running: number; queued: number; limit: number } {
  return { running, queued: queue.length, limit: maxConcurrent };
}

/**
 * Run `fn` only when a concurrency slot is available.
 * Returns whatever `fn` returns; re-throws if `fn` throws.
 */
export async function httpGate<T>(fn: () => Promise<T>): Promise<T> {
  // Wait for a slot
  if (running >= maxConcurrent) {
    await new Promise<void>(resolve => queue.push(resolve));
  }

  running++;
  try {
    return await fn();
  } finally {
    running--;
    if (queue.length > 0) {
      queue.shift()!();
    }
  }
}
